<template>
    <div class="mx-auto max-w-[600px] px-3 pt-6 sm:px-6 sm:pt-12">
        <!-- Page header -->
        <div class="flex items-center gap-2 mb-16">
            <CreditCard class="size-6 text-new-neutral-800" />
            <h1 class="text-3xl text-new-neutral-800">Billing</h1>
        </div>

        <!-- Tab nav -->
        <BillingTabsV2 active="invoices" class="mb-8" />

        <!-- Invoices table -->
        <Deferred data="invoices">
            <template #fallback>
                <div class="bg-white border shadow-md min-h-[500px] overflow-hidden">
                    <div class="border-b h-10 flex items-center px-3 gap-6">
                        <div v-for="header in headers" :key="header.id" class="h-3 w-16 rounded-none bg-new-neutral-200 animate-pulse"></div>
                    </div>
                    <div class="p-3 space-y-3">
                        <div v-for="i in 3" :key="i" class="flex items-center gap-6">
                            <div class="h-4 w-32 rounded-none bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-4 w-20 rounded-none bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-4 w-16 rounded-none bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-4 w-12 rounded-none bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-4 w-16 rounded-none bg-new-neutral-200 animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </template>

            <DataTableV2
                v-if="invoices"
                :headers="headers"
                :items="invoices"
                clickable
                @row-click="(row) => openInvoice((row as IInvoice).id)"
            >
                <template #item-type="{ data }">
                    <span class="text-xs text-foreground">{{ describeType(data as string) }}</span>
                </template>

                <template #item-issued_at="{ data }">
                    <span class="text-xs text-foreground">
                        {{ data ? format(new Date(data as string), 'M/d/yyyy') : '' }}
                    </span>
                </template>

                <template #item-usage="{ data }">
                    <span class="text-xs text-new-neutral-600">{{ data || '—' }}</span>
                </template>

                <template #item-amount_currency="{ data }">
                    <span class="text-xs text-new-neutral-600">{{ data }}</span>
                </template>

                <template #item-status="{ data }">
                    <span class="text-xs text-new-neutral-400">
                        {{ describeStatus(data as string | undefined) }}
                    </span>
                </template>

                <template #item-download="{ row }">
                    <button
                        type="button"
                        class="inline-flex items-center justify-center text-new-neutral-500 hover:text-foreground transition-colors"
                        :aria-label="`Download invoice ${(row as IInvoice).internal_id}`"
                        @click.stop="downloadInvoice((row as IInvoice).id)"
                    >
                        <Download class="size-4" />
                    </button>
                </template>

                <template #empty>
                    <div class="flex flex-col items-center justify-center gap-5 py-8">
                        <svg
                            width="80"
                            height="80"
                            viewBox="0 0 80 80"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M30 80H10V70H30V80ZM70 70V80H50V70H70ZM50 70H30V50H50V70ZM20 60H0V50H20V60ZM80 60H60V50H80V60ZM30 50H20V30H30V50ZM60 50H50V30H60V50ZM20 30H10V0H20V30ZM50 30H30V0H50V30ZM70 30H60V0H70V30Z"
                                class="fill-new-neutral-100"
                            />
                        </svg>
                        <div class="flex flex-col items-center gap-1.5">
                            <p class="text-sm font-semibold text-foreground">Invoices</p>
                            <p class="text-xs text-muted-foreground">
                                Showing
                                <span class="font-semibold text-foreground">{{ invoices.total_records }}</span>
                                out of
                                <span class="font-semibold text-foreground">{{ invoices.total_records }}</span>
                                results
                            </p>
                        </div>
                    </div>
                </template>
            </DataTableV2>
        </Deferred>

        <!-- Legacy invoices link -->
        <div v-if="showLegacyInvoices" class="mt-6 text-sm text-muted-foreground">
            <span class="font-medium">All invoices created before August 1, 2025, are now in a separate section.</span>
            <inertia-link
                class="ml-1 font-medium text-new-blue-500 hover:text-new-blue-600"
                :href="route('billing.legacy-invoices.index')"
            >
                Click here
            </inertia-link>
            to view them.
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, PropType } from 'vue';
import axios from 'axios';
import { Deferred } from '@inertiajs/vue3';
import { format } from 'date-fns';
import { route } from 'ziggy-js';
import { CreditCard, Download } from 'lucide-vue-next';

import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue';
import { DataTableV2 } from '@/components/ui/data-table-v2';
import type { IDataTableHeader, IPaginationData } from '@/components/ui/data-table-v2';
import IInvoice from '@/types/Invoices/IInvoice';
import usePdf from '@/composables/usePdf';
import BillingTabsV2 from '../components/BillingTabsV2.vue';

defineOptions({
    layout: DefaultLayoutV2,
});

const props = defineProps({
    invoices: {
        type: Object as PropType<IPaginationData<IInvoice> | null>,
        default: null,
    },
});

const { download } = usePdf();

const showLegacyInvoices = ref(false);

onMounted(async () => {
    const res = await axios.get(route('billing.legacy-invoices-show.index'));
    if (res.data.success) {
        showLegacyInvoices.value = res.data.data.showLegacyInvoices;
    }
});

const hasInvoices = computed(() => (props.invoices?.data?.length ?? 0) > 0);

const headers = computed<IDataTableHeader[]>(() => {
    const cols: IDataTableHeader[] = [
        { text: 'Description', id: 'type', sortable: true, sortableId: 'popis' },
        { text: 'Date', id: 'issued_at', align: 'left', sortable: true, sortableId: 'datVyst' },
        { text: 'Usage', id: 'usage', align: 'left' },
        { text: 'Total', id: 'amount_currency', align: 'left', sortable: true, sortableId: 'sumCelkemMen' },
        { text: 'Status', id: 'status', align: 'right', sortable: true, sortableId: 'stavUhrK' },
    ];

    if (hasInvoices.value) {
        cols.push({ text: '', id: 'download', align: 'right' });
    }

    return cols;
});

const describeType = (type: string): string => {
    if (type === 'PRO') return 'PRO Subscription';
    if (type === 'Team') return 'Team Subscription';
    if (type === 'Plugin') return 'Plugin Subscription';
    return type;
};

const describeStatus = (status: string | undefined): string => {
    if (!status) return 'Issued';
    if (status === 'stavUhr.castUhr') return 'Paid (partially)';
    return 'Paid';
};

const downloadInvoice = (id: number) => {
    const url = route('billing.invoices.download-pdf', { invoice: id });
    download(url, 'Invoice');
};

// Open the PDF in a new browser tab so the user can preview it without
// triggering a download. Browsers that can't render PDFs inline will fall
// back to downloading.
const openInvoice = (id: number) => {
    const url = route('billing.invoices.download-pdf', { invoice: id });
    window.open(url, '_blank', 'noopener,noreferrer');
};
</script>
